import React, { useState, useEffect } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { translateTextOptimized } from "../../utils/OptimizedTranslationAPI";

/**
 * Modal pour afficher les détails d'une recette
 * Avec option vidéo YouTube
 * Multilingue : Français, English, ភាសាខ្មែរ (Khmer)
 * Traduction automatique des ingrédients et instructions
 */
const RecipeDetailsModal = ({ recipe, onClose }) => {
  const { t, language, translateIngredient, translateInstructions } =
    useLanguage();

  const [translatedIngredients, setTranslatedIngredients] = useState([]);
  const [translatedInstructions, setTranslatedInstructions] = useState([]);
  const [isTranslating, setIsTranslating] = useState(false);

  // Traduction automatique via API quand la langue change
  useEffect(() => {
    if (!recipe) return;
    const translateContent = async () => {
      // Pas de traduction si langue anglaise
      if (language === "en") {
        setTranslatedIngredients(formatIngredients(recipe.ingredients, false));
        setTranslatedInstructions(
          formatInstructions(recipe.instructions, false),
        );
        return;
      }

      setIsTranslating(true);

      try {
        // TRADUCTION COMPLÈTE AVEC API (Tous les mots - illimité!)
        const ingredientLines =
          recipe.ingredients?.split("\n").filter((line) => line.trim()) || [];

        // Traduire TOUS les ingrédients avec l'API optimisée (dictionnaire massif + APIs)
        const translatedIngList = await Promise.all(
          ingredientLines.map(async (ingredient) => {
            // Utiliser l'API optimisée avec dictionnaire de 1,000,000+ mots
            const optimizedTranslation = await translateTextOptimized(
              ingredient,
              language,
              "auto",
            );
            return optimizedTranslation || ingredient;
          }),
        );

        // Traduire TOUTES les instructions avec l'API optimisée
        const instructionLines =
          recipe.instructions?.split("\n").filter((line) => line.trim()) || [];

        const translatedInstList = await Promise.all(
          instructionLines.map(async (instruction) => {
            // Utiliser l'API optimisée avec dictionnaire de 1,000,000+ mots
            const optimizedTranslation = await translateTextOptimized(
              instruction,
              language,
              "auto",
            );
            return optimizedTranslation || instruction;
          }),
        );

        setTranslatedIngredients(translatedIngList);
        setTranslatedInstructions(translatedInstList);
      } catch (error) {
        console.error("Translation error:", error);
        // Fallback au dictionnaire local
        setTranslatedIngredients(formatIngredients(recipe.ingredients, true));
        setTranslatedInstructions(
          formatInstructions(recipe.instructions, true),
        );
      } finally {
        setIsTranslating(false);
      }
    };

    translateContent();
  }, [language, recipe]);

  const formatInstructions = (instructions, useLocal = false) => {
    if (!instructions) return [];
    const lines = instructions.split("\n").filter((line) => line.trim());
    if (useLocal && (language === "km" || language === "fr")) {
      return lines.map((line) => translateInstructions(line));
    }
    return lines;
  };

  const formatIngredients = (ingredients, useLocal = false) => {
    if (!ingredients) return [];
    const lines = ingredients.split("\n").filter((line) => line.trim());
    if (useLocal && (language === "km" || language === "fr")) {
      return lines.map((ingredient) => translateIngredient(ingredient));
    }
    return lines;
  };

  // Early return après les hooks
  if (!recipe) return null;

  const handleWatchVideo = () => {
    if (recipe.youtube) {
      window.open(recipe.youtube, "_blank");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header avec Image */}
        <div className="relative h-64 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-t-3xl overflow-hidden">
          {recipe.image ? (
            <img
              src={recipe.image}
              alt={recipe.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-8xl">🍽️</span>
            </div>
          )}

          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

          {/* Bouton fermer */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-md hover:bg-white/30 rounded-full flex items-center justify-center transition-all text-white hover:rotate-90 duration-300"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Badge source */}
          {recipe.source === "TheMealDB" && (
            <div className="absolute top-4 left-4">
              <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg flex items-center space-x-2">
                <span>🌐</span>
                <span>Internet</span>
              </div>
            </div>
          )}

          {/* Titre en overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <h2 className="text-4xl font-bold text-white mb-2 drop-shadow-lg">
              {recipe.title}
            </h2>
            {recipe.description && (
              <p className="text-white/90 text-lg drop-shadow">
                {recipe.description}
              </p>
            )}
          </div>
        </div>

        {/* Contenu */}
        <div className="p-8">
          {/* Statistiques de correspondance */}
          {recipe.matchPercentage !== undefined && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6 border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-bold text-gray-900">
                  {t("correspondence")}
                </span>
                <span className="text-2xl font-bold text-blue-600">
                  {recipe.matchPercentage}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden mb-3">
                <div
                  className={`h-full transition-all duration-500 ${
                    recipe.matchPercentage >= 80
                      ? "bg-green-500"
                      : recipe.matchPercentage >= 60
                        ? "bg-yellow-500"
                        : "bg-orange-500"
                  }`}
                  style={{ width: `${recipe.matchPercentage}%` }}
                ></div>
              </div>
              <div className="text-sm text-gray-600">
                ✅ {t("youHaveIngredients")} {recipe.matchCount} {t("outOf")}{" "}
                {recipe.totalIngredients}
              </div>
            </div>
          )}

          {/* Bouton vidéo YouTube */}
          {recipe.youtube && (
            <div className="mb-6">
              <button
                onClick={handleWatchVideo}
                className="w-full px-6 py-4 bg-gradient-to-r from-red-600 to-red-500 text-white font-semibold rounded-xl hover:from-red-700 hover:to-red-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center space-x-3"
              >
                <svg
                  className="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
                <span className="text-lg">{t("watchVideo")}</span>
              </button>
            </div>
          )}

          {/* Ingrédients et Instructions */}
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6">
            {isTranslating && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/90 rounded-2xl min-h-[280px]">
                <div className="w-10 h-10 rounded-full border-2 border-orange-500/30 border-t-orange-500 animate-spin" />
                <p className="text-gray-700 font-medium">{t("translating")}</p>
              </div>
            )}
            {/* Ingrédients */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="text-3xl">🥘</span>
                {t("ingredients")}
                {isTranslating && (
                  <span className="text-sm text-gray-500 animate-pulse">
                    ⏳
                  </span>
                )}
              </h3>
              <ul className="space-y-3">
                {translatedIngredients.map((ingredient, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <span className="text-green-600 mr-3 mt-1 text-lg font-bold">
                      •
                    </span>
                    <span className="text-base">{ingredient}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Instructions */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="text-3xl">👨‍🍳</span>
                {t("instructions")}
                {isTranslating && (
                  <span className="text-sm text-gray-500 animate-pulse">
                    ⏳
                  </span>
                )}
              </h3>
              <ol className="space-y-3">
                {translatedInstructions.map((instruction, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <span className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-sm leading-relaxed">
                      {instruction}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Informations supplémentaires */}
          {(recipe.category || recipe.area) && (
            <div className="mt-6 flex gap-3">
              {recipe.category && (
                <div className="flex-1 bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                  <div className="text-2xl mb-1">🏷️</div>
                  <div className="text-sm text-gray-600">{t("category")}</div>
                  <div className="text-lg font-bold text-purple-900">
                    {recipe.category}
                  </div>
                </div>
              )}
              {recipe.area && (
                <div className="flex-1 bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
                  <div className="text-2xl mb-1">🌍</div>
                  <div className="text-sm text-gray-600">{t("origin")}</div>
                  <div className="text-lg font-bold text-orange-900">
                    {recipe.area}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bouton fermer en bas */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-xl transition-all"
            >
              {t("closeButton")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeDetailsModal;
